import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, idemKey } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Est } from "../lib/currency";
import { payWithRazorpay, payErr } from "../lib/razorpay";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Coins, Check } from "lucide-react";
import { toast } from "sonner";

export const PACKAGE_TERMS = [
  "Bidz are a bidding-access fee. They are separate from the price you pay for an item you win.",
  "Bidz are deducted only when a bid is accepted. Bidz spent are not returned if you are outbid or the auction completes.",
  "If AuctionBulls cancels or invalidates an auction, the Bidz you spent on it are restored automatically.",
  "Unused purchased Bidz cannot be converted into cash, except where a refund is required by applicable law.",
];

export const perBidz = (p) => p.price / p.bidz;

// product: { kind: "package"|"pass", id?, name, price, currency, bidz?, validity_days? }
export function BuyConfirmDialog({ product, listing, terms, onClose, onBought }) {
  const [busy, setBusy] = useState(false);
  const key = useRef(idemKey());
  useEffect(() => { key.current = idemKey(); }, [product]);
  const buy = async () => {
    setBusy(true);
    try {
      const body = product.kind === "pass" ? { kind: "pass", listing_id: listing.id } : { kind: "package", package_id: product.id };
      const r = await payWithRazorpay(body, key.current);
      toast.success(product.kind === "pass" ? "Auction Pass activated" : `${r.bidz} Bidz added`, { description: `${product.name} — ${money(product.price)} paid` });
      onBought?.(r);
    } catch (e) {
      toast.error(payErr(e));
    } finally { setBusy(false); }
  };
  const isPass = product?.kind === "pass";
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bidz-buy-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle>Confirm {product?.name}</DialogTitle></DialogHeader>
        {product && (
          <div className="space-y-4 text-sm">
            <div className="rounded-xl bg-slate-900 text-white p-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold">You get</div>
                <div className="text-2xl font-extrabold" data-testid="bidz-buy-amount">{isPass ? "Unlimited bids" : `${product.bidz} Bidz`}</div>
                <div className="text-xs text-slate-400">{isPass ? `Only on: ${listing?.title}` : `Valid ${product.validity_days} days from payment`}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">You pay</div>
                <div className="text-2xl font-extrabold" data-testid="bidz-buy-price">{money(product.price, product.currency)}</div>
                <Est inr={product.price} className="text-slate-300" />
              </div>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400" data-testid="bidz-buy-terms">
              {(terms || PACKAGE_TERMS).map(t => <li key={t} className="flex gap-2"><Check className="h-3.5 w-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />{t}</li>)}
            </ul>
            <div className="text-[11px] font-semibold text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300 rounded-lg px-3 py-2" data-testid="bidz-buy-gateway-note">
              Secure payment by Razorpay (test mode). You are charged in INR; other currencies are estimates.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bidz-buy-cancel">Cancel</Button>
          <Button onClick={buy} disabled={busy} data-testid="bidz-buy-confirm" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
            {busy ? "Opening checkout…" : `Pay ${product ? money(product.price, product.currency) : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PackageCard({ p, best, onBuy }) {
  return (
    <div data-testid={`bidz-package-${p.code}`} className={`relative rounded-2xl border p-6 bg-white dark:bg-slate-900 flex flex-col transition-transform duration-200 hover:-translate-y-1 ${best ? "border-amber-500 shadow-xl" : "border-slate-200 dark:border-slate-800"}`}>
      {best && <span className="absolute -top-3 left-6 bg-amber-500 text-slate-900 text-[10px] uppercase font-extrabold tracking-widest px-2 py-1 rounded">Best value</span>}
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">{p.name}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-4xl font-extrabold">{p.bidz}</span><span className="text-sm font-semibold text-slate-500">Bidz</span>
      </div>
      <div className="mt-1 text-2xl font-bold flex items-baseline gap-2">{money(p.price, p.currency)} <Est inr={p.price} /></div>
      <div className="mt-1 text-xs text-slate-500" data-testid={`bidz-package-per-${p.code}`}>{money(perBidz(p), p.currency)} per Bidz · valid {p.validity_days} days</div>
      {p.description && <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">{p.description}</p>}
      <Button onClick={() => onBuy(p)} data-testid={`bidz-buy-${p.code}`} className="mt-6 bg-slate-900 hover:bg-slate-800 text-white dark:bg-amber-500 dark:text-slate-900">
        <Coins className="h-4 w-4 mr-2" /> Buy {p.name}
      </Button>
    </div>
  );
}

export function BidzPackages() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pkgs, setPkgs] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/bidz/packages").then(r => setPkgs(r.data)).catch(() => toast.error("Could not load packages")); }, []);
  const best = pkgs.length ? pkgs.reduce((a, b) => (perBidz(b) < perBidz(a) ? b : a)) : null;
  const onBuy = (p) => (user ? setSel({ ...p, kind: "package" }) : nav("/signin?next=/bidz"));
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="bidz-packages-grid">
        {pkgs.map(p => <PackageCard key={p.id} p={p} best={best?.id === p.id} onBuy={onBuy} />)}
      </div>
      <BuyConfirmDialog product={sel} onClose={() => setSel(null)} onBought={() => setSel(null)} />
    </>
  );
}
